export function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(typeof d === 'string' ? d.replace(' ', 'T') : d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
export const shortDate = fmtDate;

export function feeLabel(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  if (n === 0) return 'Free';
  if (n >= 1e6) return `€${(n / 1e6).toFixed(1)}m`;
  if (n >= 1e3) return `€${(n / 1e3).toFixed(0)}k`;
  return `€${n}`;
}

export function cap(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function vVal(v) {
  if (v == null) return null;
  if (typeof v !== 'object') return v;
  if ('average' in v) return v.average;
  if ('total' in v) return v.total;
  if ('count' in v) return v.count;
  if ('value' in v) return v.value;
  if (v.all && typeof v.all === 'object' && 'count' in v.all) return v.all.count;
  return null;
}

export function statVal(details, code) {
  if (!Array.isArray(details)) return null;
  const d = details.find((x) => x?.type?.code === code || x?.code === code);
  return d ? vVal(d.value ?? d) : null;
}

export function goalsOf(v) { return vVal(v?.goals ?? v?.scored ?? v); }
export function penOf(v) { return vVal(v?.penalties ?? v?.penalty_goals ?? v?.penaltyGoals); }
export function ratingOf(v) {
  const r = vVal(v);
  if (r == null) return '—';
  const n = Number(r);
  return isFinite(n) ? n.toFixed(2) : String(r);
}

export function positionFamily(pos) {
  const p = (pos || '').toUpperCase();
  if (p.includes('GK') || p.includes('GOAL')) return 'GK';
  if (p.includes('DEF') || p.includes('BACK') || /^D/.test(p)) return 'DEF';
  if (p.includes('ATT') || p.includes('FW') || p.includes('ST') || p.includes('FORWARD')) return 'ATT';
  if (p.includes('MID') || p.includes('MIDFIELD') || /^M/.test(p)) return 'MID';
  return 'MID';
}

export const POSITION_PILL = { GK: 'i-pos-gk', DEF: 'i-pos-def', MID: 'i-pos-mid', ATT: 'i-pos-att' };

function seasonFromStat(st) {
  const d = st?.details || [];
  const find = (code) => d.find((x) => x?.type?.code === code || x?.code === code);
  const valOf = (det) => { const v = det?.value; if (v == null) return null; if (typeof v === 'object') return v.total ?? v.average ?? v.count ?? v.value ?? null; return v; };
  const get = (code) => valOf(find(code));
  const goalVal = find('goals')?.value || {};
  const minutes = Number(get('minutes-played') || get('minutes') || 0);
  const goals = Number(goalVal.total ?? goalVal.goals ?? get('goals') ?? 0);
  const assists = Number(get('assists') || 0);
  return {
    seasonId: st?.season?.id ?? st?.season_id,
    seasonName: st?.season?.name || '—',
    isCurrent: !!st?.season?.is_current,
    leagueId: st?.season?.league_id ?? st?.league_id,
    leagueName: null,
    teamId: st?.team_id ?? st?.team?.id,
    teamName: null,
    teamImage: null,
    jerseyNumber: st?.jersey_number,
    appearances: get('appearances') ?? get('matches'),
    lineups: get('lineups'),
    bench: get('bench'),
    minutes: get('minutes-played') ?? get('minutes'),
    goals,
    penaltyGoals: goalVal.penalties ?? null,
    assists,
    rating: get('rating'),
    shotsTotal: get('shots-total') ?? get('shots_total') ?? get('shots'),
    shotsOnTarget: get('shots-on-target') ?? get('shots_on_target'),
    shotsOffTarget: get('shots-off-target'),
    passes: get('passes'),
    accuratePasses: get('accurate-passes') ?? get('accurate_passes'),
    passAccuracyPct: get('accurate-passes-percentage') ?? get('pass_accuracy') ?? get('accurate_passes_pct'),
    tackles: get('tackles'),
    interceptions: get('interceptions'),
    clearances: get('clearances'),
    duelsWon: get('duels-won') ?? get('duels_won'),
    duelsTotal: get('total-duels') ?? get('duels_total'),
    aerWon: get('aeriels-won'),
    crosses: get('total-crosses'),
    dribbleAttempts: get('dribble-attempts'),
    cleanSheets: get('cleansheets'),
    teamWins: get('team-wins'),
    teamDraws: get('team-draws'),
    teamLost: get('team-lost'),
    yellowCards: get('yellow-cards') ?? get('yellow_cards'),
    goalsPer90: minutes > 0 ? (goals / minutes * 90).toFixed(2) : null,
    assistsPer90: minutes > 0 ? (assists / minutes * 90).toFixed(2) : null,
  };
}

export function normalizePlayer(data) {
  const p = data?.profile || {};
  const team = data?.currentTeam?.overview || data?.currentTeam || p?.team || {};
  const stats = Array.isArray(p?.statistics) ? p.statistics : [];
  const seasons = stats.map(seasonFromStat);

  const teamMap = new Map();
  const addTeam = (t) => { if (t?.id) teamMap.set(t.id, { name: t.name, image: t.image_path || t.image }); };
  addTeam(data?.currentTeam?.overview);
  (Array.isArray(data?.transfers) ? data.transfers : Array.isArray(p?.transfers) ? p.transfers : []).forEach((t) => { addTeam(t?.fromteam || t?.from_team); addTeam(t?.toteam || t?.to_team); });
  const leagueMap = new Map();
  (Array.isArray(p?.trophies) ? p.trophies : []).forEach((tr) => { if (tr?.league?.id) leagueMap.set(tr.league.id, tr.league.name); });
  seasons.forEach((s) => {
    s.seasonLabel = seasonShortLabel(s);
    if (s.teamId != null) { const tm = teamMap.get(s.teamId); if (tm) { s.teamName = tm.name; s.teamImage = tm.image; } }
    if (s.leagueId != null && leagueMap.has(s.leagueId)) s.leagueName = leagueMap.get(s.leagueId);
  });

  const current = pickDefaultSeason(seasons);

  let goalsT = 0, appsT = 0, minT = 0, assistsT = 0, shotsT = 0, yellowT = 0, accSum = 0, accN = 0, rateSum = 0, rateN = 0;
  seasons.forEach((s) => {
    goalsT += Number(s.goals || 0);
    assistsT += Number(s.assists || 0);
    appsT += Number(s.appearances || 0);
    minT += Number(s.minutes || 0);
    shotsT += Number(s.shotsTotal || 0);
    yellowT += Number(s.yellowCards || 0);
    if (s.passAccuracyPct != null) { accSum += Number(s.passAccuracyPct); accN++; }
    if (s.rating != null) { rateSum += Number(s.rating); rateN++; }
  });

  const transfers = (Array.isArray(data?.transfers) ? data.transfers : Array.isArray(p?.transfers) ? p.transfers : [])
    .map((t) => {
      const from = t?.fromteam || t?.from_team || t?.from || {};
      const to = t?.toteam || t?.to_team || t?.to || {};
      const fee = t?.amount ?? t?.fee ?? t?.transfer_fee;
      return {
        date: t?.date || t?.transfer_date,
        from: { id: from?.id, name: from?.name, image: from?.image_path || from?.image },
        to: { id: to?.id, name: to?.name, image: to?.image_path || to?.image },
        fee,
        feeLabel: feeLabel(fee),
        type: t?.type?.name || t?.type,
      };
    });

  const lastTransfer = transfers[0] || null;

  const trophiesList = (Array.isArray(p?.trophies) ? p.trophies : [])
    .map((tr) => {
      const pos = tr?.trophy?.position ?? tr?.position;
      const isWin = pos === 1 || !!tr?.won;
      return {
        result: isWin ? 'Winner' : 'Runner-up',
        clubImage: tr?.team?.image_path ?? tr?.club?.image_path,
        leagueId: tr?.league?.id ?? tr?.league_id,
        leagueImage: tr?.league?.image_path,
        league: tr?.league?.name,
        season: tr?.season?.name || tr?.season,
      };
    });
  const wins = trophiesList.filter((t) => t.result === 'Winner').length;

  const rawLineups = Array.isArray(data?.lineups) ? data.lineups : Array.isArray(p?.lineups) ? p.lineups : [];
  const recent = rawLineups
    .filter((l) => l && l.fixture && l.fixture.starting_at)
    .map((l) => {
      const f = l.fixture;
      const parts = Array.isArray(f.participants) ? f.participants : [];
      const home = parts.find((x) => x?.meta?.location === 'home') || parts[0] || {};
      const away = parts.find((x) => x?.meta?.location === 'away') || parts[1] || {};
      const cur = (Array.isArray(f.scores) ? f.scores : []).filter((s) => s?.description === 'CURRENT');
      const hg = cur.find((s) => s?.participant_id === home.id)?.score?.goals;
      const ag = cur.find((s) => s?.participant_id === away.id)?.score?.goals;
      return {
        id: l?.fixture_id || f?.id,
        date: f.starting_at,
        competition: f.league?.name,
        leagueId: f.league?.id,
        home: { id: home.id, name: home.name, image: home.image_path },
        away: { id: away.id, name: away.name, image: away.image_path },
        score: hg != null && ag != null ? `${hg}-${ag}` : null,
        result_info: f.result_info,
        role: l?.type?.code === 'bench' || l?.type?.name === 'Bench' ? 'Bench' : 'Started',
        jersey: l?.jersey_number,
      };
    })
    .sort((a, b) => new Date((b.date || '').replace(' ', 'T')) - new Date((a.date || '').replace(' ', 'T')));

  return {
    id: p?.id,
    name: p?.display_name || [p?.firstname, p?.lastname].filter(Boolean).join(' ') || '—',
    fullName: [p?.fullname, p?.display_name, [p?.firstname, p?.lastname].filter(Boolean).join(' ')].filter(Boolean)[0],
    image: p?.image_path,
    position: p?.position?.name || p?.detailedPosition?.name,
    positionCode: p?.position?.code,
    detailedPosition: p?.detailedPosition?.name || p?.position?.name,
    nationality: { name: p?.nationality?.name || p?.country?.name, image: p?.nationality?.image_path || p?.country?.image_path },
    dateOfBirth: p?.birthdate || p?.date_of_birth,
    age: p?.age ?? (() => { const d = p?.birthdate || p?.date_of_birth; if (!d) return null; const t = new Date(d).getTime(); return isNaN(t) ? null : Math.floor((Date.now() - t) / 31557600000); })(),
    height: p?.height,
    weight: p?.weight,
    preferredFoot: p?.foot || p?.preferred_foot,
    gender: p?.gender,
    city: p?.city?.name || p?.city,
    commonName: p?.common_name,
    currentClub: {
      id: team?.id,
      name: team?.name,
      image: team?.image_path,
      type: team?.type,
      founded: team?.founded,
      jerseyNumber: current?.jerseyNumber ?? p?.jersey_number ?? p?.squad_number,
      captain: !!p?.captain,
      joined: p?.joined,
      contractUntil: p?.contract_until ?? p?.contract_expires,
    },
    currentSeasonStats: current,
    allSeasonStats: seasons,
    career: {
      seasonsPlayed: new Set(seasons.map((s) => s.seasonId)).size,
      clubsPlayedFor: new Set(stats.map((s) => s?.team_id ?? s?.team?.id).filter(Boolean)).size,
      leaguesPlayedIn: new Set(seasons.map((s) => s.leagueId).filter(Boolean)).size,
      appearances: appsT,
      minutesPlayed: minT,
      goals: goalsT,
      assists: assistsT,
      averageRating: rateN ? (rateSum / rateN).toFixed(2) : null,
      shotsTotal: shotsT,
      passAccuracyPct: accN ? Math.round(accSum / accN) : null,
      yellowCards: yellowT,
      goalsPer90: minT > 0 ? (goalsT / minT * 90).toFixed(2) : null,
    },
    recentMatches: recent,
    transfers,
    lastTransferFee: lastTransfer ? { amount: lastTransfer.fee, label: lastTransfer.feeLabel, date: lastTransfer.date } : null,
    trophies: { total: trophiesList.length, wins, runnerUp: trophiesList.length - wins, list: trophiesList },
    standings: data?.league?.standings || p?.standings || null,
  };
}

export function parseSeasonYears(name) {
  if (!name) return null;
  const s = String(name).trim().replace(/\s/g, '');
  let m = s.match(/^(\d{4})\/(\d{4})$/);
  if (m) return { start: +m[1], end: +m[2] };
  m = s.match(/^(\d{4})\/(\d{2})$/);
  if (m) return { start: +m[1], end: 2000 + +m[2] };
  m = s.match(/^(\d{4})$/);
  if (m) return { start: +m[1], end: +m[1] };
  return null;
}

export function currentSeasonYears(date = new Date()) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const start = m >= 6 ? y : y - 1;
  return { start, end: start + 1 };
}

export function seasonStartYear(s) {
  const y = parseSeasonYears(typeof s === 'string' ? s : s?.seasonName);
  return y ? y.start : null;
}
export function seasonEndYear(s) {
  const y = parseSeasonYears(typeof s === 'string' ? s : s?.seasonName);
  return y ? y.end : null;
}

export function seasonShortLabel(s) {
  const name = typeof s === 'string' ? s : s?.seasonName;
  const y = parseSeasonYears(name);
  if (!y) return name || '—';
  if (y.start === y.end) return String(y.start);
  return `${y.start}/${String(y.end).slice(2)}`;
}

export function normalizeSeasonName(name) {
  if (!name) return null;
  const s = String(name).trim();
  let m = s.match(/^(\d{4})\/(\d{2,4})$/);
  if (m) return `${m[1]}/${m[2].slice(-2)}`;
  m = s.match(/^(\d{4})-(\d{2,4})$/);
  if (m) return `${m[1]}/${m[2].slice(-2)}`;
  return s;
}

export function sortSeasonsDesc(seasons) {
  return [...(seasons || [])].sort((a, b) => {
    const ea = seasonEndYear(a) ?? 0, eb = seasonEndYear(b) ?? 0;
    if (eb !== ea) return eb - ea;
    const sa = seasonStartYear(a) ?? 0, sb = seasonStartYear(b) ?? 0;
    return sb - sa;
  });
}

export function pickDefaultSeason(seasons) {
  const list = seasons || [];
  if (!list.length) return null;
  const cy = currentSeasonYears();
  const splitName = `${cy.start}/${cy.end}`;
  const calName = `${cy.start}`;
  const norm = (n) => String(n || '').replace(/\s/g, '');
  const byDate = list.find((s) => {
    const n = norm(s.seasonName);
    return n === splitName || n === calName;
  });
  if (byDate) return byDate;
  const byFlag = list.find((s) => s.isCurrent);
  if (byFlag) return byFlag;
  return sortSeasonsDesc(list)[0];
}

export function fixtureInSeason(dateStr, season) {
  if (!dateStr) return true;
  const y = parseSeasonYears(season?.seasonName);
  if (!y) return true;
  const d = new Date(String(dateStr).replace(' ', 'T'));
  if (isNaN(d)) return true;
  const yr = d.getFullYear();
  return yr >= y.start && yr <= y.end;
}
